import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import { defaultMemoize } from 'reselect';
import _get from 'lodash/get';
import _filter from 'lodash/filter';
import _castArray from 'lodash/castArray';
import _includes from 'lodash/includes';
import _reduce from 'lodash/reduce';
import _isNil from 'lodash/isNil';
import _last from 'lodash/last';
import _keys from 'lodash/keys';
import _map from 'lodash/map';
import _find from 'lodash/find';
import Money from '../../utils/money';
import { EMPTY_OBJECT, EMPTY_ARRAY, NO_DATA } from '../../app.constants/index';
import { formatCurrencyWithMoneyFormat, getNumber, findArrayItem, arraySumBasedOnKey } from '../utils';
import * as DeskingHelpers from '../helpers/desking.helpers';
import { EXPIRED_PROGRAM_FLAG } from '../constants/marketScan.constants';
import { PAYMENT_FREQUENCY_SHORT_DISPLAY, GROSS_TYPES, PAYMENT_TYPE_DOWNPAYMENT_TYPE_MAPPING, RESIDUAL_DISPLAY_TYPE, PAYMENT_TYPE_VS_TAX_TYPE } from '../constants/desking.constants';
import { INTERNAL_FEE_CODES } from '../../constants/retail/fee.constants';
import { DISCLOSURE_TYPES } from '../../constants/deal/fnis';
import { LEASE_CONTRACT } from '../constants/feeUpdateTable.constants';
export function getAPR() {
  var columnData = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  var downPaymentInfo = getSelectedDownPaymentInfo(columnData);
  return _get(downPaymentInfo, 'apr.apr') || 0;
}
export function getLenderRateType() {
  var columnData = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  var downPaymentInfo = getSelectedDownPaymentInfo(columnData);
  return _get(downPaymentInfo, 'apr.lenderRateType') || 0;
}
export function getRateType() {
  var columnData = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  var downPaymentInfo = getSelectedDownPaymentInfo(columnData);
  return _get(downPaymentInfo, 'apr.rateType') || 0;
}
export function getMRM() {
  var columnData = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  var downPaymentInfo = getSelectedDownPaymentInfo(columnData);
  return _get(downPaymentInfo, 'residual.crvMRM') || 0;
}
export function getResidualDisplayType() {
  var columnData = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  var downPaymentInfo = getSelectedDownPaymentInfo(columnData) || {};
  return _get(downPaymentInfo, 'residual.residualDisplayType');
}
export function getResidualPercent() {
  var columnData = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  var downPaymentInfo = getSelectedDownPaymentInfo(columnData) || {};
  return _get(downPaymentInfo, 'residual.basePercentage') || 0;
}
export function getTotalResidualPercent() {
  var columnData = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : EMPTY_OBJECT;
  var downPaymentInfo = getSelectedDownPaymentInfo(columnData) || EMPTY_OBJECT;
  return _get(downPaymentInfo, 'residual.totalPercentage') || 0;
}
export function getTotalResidualValue() {
  var columnData = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : EMPTY_OBJECT;
  var downPaymentInfo = getSelectedDownPaymentInfo(columnData) || EMPTY_OBJECT;
  return _get(downPaymentInfo, 'residual.totalValue') || 0;
}
export function getResidualValueBasedOnDisplayType() {
  var columnData = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : EMPTY_OBJECT;
  var residual = getResidualData(columnData) || EMPTY_OBJECT;
  var residualDisplayType = residual.residualDisplayType;

  if (residualDisplayType === RESIDUAL_DISPLAY_TYPE.AMOUNT) {
    return _get(residual, 'totalValue');
  }

  return _get(residual, 'totalPercentage');
}
export function getTotalYearlyMiles() {
  var columnData = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : EMPTY_OBJECT;
  var downPaymentInfo = getSelectedDownPaymentInfo(columnData) || EMPTY_OBJECT;
  return _get(downPaymentInfo, 'yearlyMiles.totalValue') || 0;
}
export function getTotalMileagePenalty() {
  var columnData = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : EMPTY_OBJECT;
  var downPaymentInfo = getSelectedDownPaymentInfo(columnData) || EMPTY_OBJECT;
  return _get(downPaymentInfo, 'yearlyMiles.totalPenalty') || 0;
}
export function isPenaltyPerMileOverridden(columnData) {
  var downPaymentInfo = getSelectedDownPaymentInfo(columnData) || EMPTY_OBJECT;
  return _get(downPaymentInfo, 'yearlyMiles.penaltyPerMileOverridden') || false;
}
export function isAprBuyRateOverridden(columnData) {
  var downPaymentInfo = getSelectedDownPaymentInfo(columnData) || EMPTY_OBJECT;
  return _get(downPaymentInfo, 'apr.buyRateOverridden') || false;
}
export function getReserveMethod(columnData) {
  var downPaymentInfo = getSelectedDownPaymentInfo(columnData) || EMPTY_OBJECT;
  return _get(downPaymentInfo, 'apr.reserveMethod') || false;
}
export function getExcessMilesPerYear() {
  var columnData = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : EMPTY_OBJECT;
  var downPaymentInfo = getSelectedDownPaymentInfo(columnData) || EMPTY_OBJECT;
  return _get(downPaymentInfo, 'yearlyMiles.additionalValue');
}
export function getCostPerMile() {
  var columnData = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : EMPTY_OBJECT;
  var downPaymentInfo = getSelectedDownPaymentInfo(columnData) || EMPTY_OBJECT;
  return _get(downPaymentInfo, 'yearlyMiles.penaltyPerMile');
}
export function getDaysToFirstPayment(columnData) {
  var downPaymentInfo = getSelectedDownPaymentInfo(columnData) || EMPTY_OBJECT;
  return _get(downPaymentInfo, 'daysToFirstPayment') || 0;
}
export function getSecurityDeposits(columnData) {
  var downPaymentInfo = getSelectedDownPaymentInfo(columnData) || EMPTY_OBJECT;
  return _get(downPaymentInfo, 'securityDepositV2') || EMPTY_ARRAY;
}
export function getPaymentFrequency(columnData) {
  return _get(columnData, 'paymentOption.paymentFrequency') || NO_DATA;
}
export function getLeaseProgramAvailabilityStatus(columnData) {
  return _get(columnData, 'leaseProgramAvailabilityStatus');
}
export function getMarkupRate(columnData) {
  var downPaymentInfo = getSelectedDownPaymentInfo(columnData) || {};
  return _get(downPaymentInfo, 'apr.markUp') || 0;
}
export function getFinanceReserve(columnData) {
  var downPaymentInfo = getSelectedDownPaymentInfo(columnData) || {};
  return _get(downPaymentInfo, 'apr.financeReserve') || 0;
}
export function getFinanceChargeFromCalcEngine(columnData) {
  var downPaymentInfo = getSelectedDownPaymentInfo(columnData) || {};
  return _get(downPaymentInfo, 'financeCharge') || 0;
}
export function getMonthlyPaymentBeforeTax(columnData) {
  var downPaymentInfo = getSelectedDownPaymentInfo(columnData) || {};
  return _get(downPaymentInfo, 'monthlyPaymentBeforeTax') || 0;
}
export function getAmountFinanaced(columnData, optionalDownPaymentId) {
  var downPaymentId = _isNil(optionalDownPaymentId) ? getSelectedDownPaymentID(columnData) : optionalDownPaymentId;
  var lenderId = getSelectedLenderId(columnData);
  var downPaymentInfo = getProgramForLenderAndDownPaymentId(columnData, lenderId, downPaymentId) || {};
  return _get(downPaymentInfo, 'amountFinanced') || 0;
}
export function getSecurityDeposit(columnData, downpaymentId) {
  var downPaymentInfo = getDownPaymentInfoForDownpaymentId(columnData, downpaymentId) || {};
  return _get(downPaymentInfo, 'securityDeposit');
} // export const getSecurityDepositId = (columnData, downpaymentId) =>
//   getDownPaymentInfoForDownpaymentId(columnData, downpaymentId)?.securityDepositId;

export var getSecurityDepositType = function getSecurityDepositType(columnData, downpaymentId) {
  var _getDownPaymentInfoFo;

  return (_getDownPaymentInfoFo = getDownPaymentInfoForDownpaymentId(columnData, downpaymentId)) === null || _getDownPaymentInfoFo === void 0 ? void 0 : _getDownPaymentInfoFo.securityDepositType;
};
export function isSecurityDepositOverridden(columnData) {
  var downPaymentInfo = getSelectedDownPaymentInfo(columnData) || {};
  return _get(downPaymentInfo, 'securityDepositOverridden') || false;
}
export function getSecurityDepositRateAdjustment(columnData) {
  var downPaymentInfo = getSelectedDownPaymentInfo(columnData) || {};
  return _get(downPaymentInfo, 'securityDepositRateAdjustment') || 0;
}
export function getLoanToValueRatio(columnData) {
  var downPaymentInfo = getSelectedDownPaymentInfo(columnData) || {};
  return _get(downPaymentInfo, 'loanToValueRatio') || 0;
}
export function getCashDeficiency(columnData) {
  var downPaymentInfo = getSelectedDownPaymentInfo(columnData) || {};
  return _get(downPaymentInfo, 'cashDeficiency') || 0;
}
export function getCashDeficiencyForDownpaymentId(columnData, downPaymentId) {
  var selectedLenderId = getSelectedLenderId(columnData);
  var program = getProgramForLenderAndDownPaymentId(columnData, selectedLenderId, downPaymentId);
  return _get(program, 'cashDeficiency') || 0;
}
export function isGeneric(columnData) {
  var downPaymentInfo = getSelectedDownPaymentInfo(columnData) || {};
  return _get(downPaymentInfo, 'generic') || 0;
}
export function getPaymentType(columnData) {
  return _get(columnData, 'paymentOption.paymentType');
}
export function getTerm(columnData) {
  return _get(columnData, 'paymentOption.value');
}

function getSelectedDownPaymentInfoForLender() {
  var columnData = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : EMPTY_OBJECT;
  var lenderId = arguments.length > 1 ? arguments[1] : undefined;
  var selectedDownPaymentId = columnData.selectedDownPaymentId;
  var termPaymentDetailsForLender = getTermPaymentDetailsForLender(columnData, lenderId) || EMPTY_OBJECT;
  return termPaymentDetailsForLender[selectedDownPaymentId];
}

export function getSelectedDownPaymentInfo() {
  var columnData = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : EMPTY_OBJECT;
  var selectedDownPaymentId = columnData.selectedDownPaymentId;
  var selectedTermPayment = getTermPaymentInfoForSelectedLender(columnData) || EMPTY_OBJECT;
  return selectedTermPayment[selectedDownPaymentId];
}

function getProgramForLenderAndDownPaymentId() {
  var columnData = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : EMPTY_OBJECT;
  var lenderId = arguments.length > 1 ? arguments[1] : undefined;
  var downPaymentId = arguments.length > 2 ? arguments[2] : undefined;
  var termPayment = getTermPaymentDetailsForLender(columnData, lenderId) || EMPTY_OBJECT;
  return termPayment[downPaymentId];
}

export function getTermPaymentDetailsForLender(columnData, lender) {
  var _ref = columnData || {},
      _ref$termPaymentDetai = _ref.termPaymentDetails,
      termPaymentDetails = _ref$termPaymentDetai === void 0 ? {} : _ref$termPaymentDetai;

  return termPaymentDetails[lender];
}
export function getSelectedTermPaymentDetailsId(columnData) {
  var _selectedTermPayment$;

  var selectedTermPayment = getTermPaymentInfoForSelectedLender(columnData) || EMPTY_OBJECT;
  var selectedDownPaymentId = columnData.selectedDownPaymentId;
  return (_selectedTermPayment$ = selectedTermPayment[selectedDownPaymentId]) === null || _selectedTermPayment$ === void 0 ? void 0 : _selectedTermPayment$.id;
}
export function getTermPaymentInfoForSelectedLender(columnData) {
  var _ref2 = columnData || {},
      selectedLenderId = _ref2.selectedLenderId,
      _ref2$termPaymentDeta = _ref2.termPaymentDetails,
      termPaymentDetails = _ref2$termPaymentDeta === void 0 ? {} : _ref2$termPaymentDeta;

  return termPaymentDetails[selectedLenderId];
}
export function getTermPaymentInfoForLender(columnData, lender) {
  return _get(columnData, ['termPaymentDetails', lender]);
}
export function getEmiAmount(columnData, downPaymentId) {
  var _columnData$selectedL = columnData.selectedLenderId,
      selectedLenderId = _columnData$selectedL === void 0 ? '' : _columnData$selectedL,
      _columnData$termPayme = columnData.termPaymentDetails,
      termPaymentDetails = _columnData$termPayme === void 0 ? EMPTY_OBJECT : _columnData$termPayme;
  var emiAmount = _get(termPaymentDetails, [selectedLenderId, downPaymentId, 'emiAmount']) || 0;
  return emiAmount;
}
export function getBaseEmiAmount(columnData, downPaymentId) {
  var _columnData$selectedL2 = columnData.selectedLenderId,
      selectedLenderId = _columnData$selectedL2 === void 0 ? '' : _columnData$selectedL2,
      _columnData$termPayme2 = columnData.termPaymentDetails,
      termPaymentDetails = _columnData$termPayme2 === void 0 ? EMPTY_OBJECT : _columnData$termPayme2;
  var emiAmount = _get(termPaymentDetails, [selectedLenderId, downPaymentId, 'baseEmiAmount']) || 0;
  return emiAmount;
}
export function isFirstPaymentWaived(columnData, downPaymentId) {
  var _columnData$selectedL3 = columnData.selectedLenderId,
      selectedLenderId = _columnData$selectedL3 === void 0 ? '' : _columnData$selectedL3,
      _columnData$termPayme3 = columnData.termPaymentDetails,
      termPaymentDetails = _columnData$termPayme3 === void 0 ? EMPTY_OBJECT : _columnData$termPayme3;
  var firstPaymentWaived = _get(termPaymentDetails, [selectedLenderId, downPaymentId, 'firstPaymentWaived']) || 0;
  return firstPaymentWaived;
}
export function getFirstPaymentWaivedAmount(columnData, downPaymentId) {
  var _columnData$selectedL4 = columnData.selectedLenderId,
      selectedLenderId = _columnData$selectedL4 === void 0 ? '' : _columnData$selectedL4,
      _columnData$termPayme4 = columnData.termPaymentDetails,
      termPaymentDetails = _columnData$termPayme4 === void 0 ? EMPTY_OBJECT : _columnData$termPayme4;
  var firstPaymentWaivedAmount = _get(termPaymentDetails, [selectedLenderId, downPaymentId, 'firstPaymentWaivedAmount']) || 0;
  return firstPaymentWaivedAmount;
}
export function getFpwManuallyOverridden(columnData, downPaymentId) {
  return _get(getDownPaymentInfoForDownpaymentId(columnData, downPaymentId), 'firstPaymentWaiverManuallyOveridden') || false;
}
export function getFormattedEmiAmount(amount, columnData) {
  var paymentOption = columnData.paymentOption;
  var paymentFrequency = paymentOption.paymentFrequency; // const divider = PAYMENT_FREQUENCY_DIVIDERS[paymentFrequency];

  var frequencyShort = PAYMENT_FREQUENCY_SHORT_DISPLAY[paymentFrequency];
  var formattedAmount = formatCurrencyWithMoneyFormat(amount);
  return "".concat(formattedAmount, " ").concat(frequencyShort);
}
export var getAPRDataForLender = function getAPRDataForLender(columnData, lenderId) {
  var paymentInfo = getSelectedDownPaymentInfoForLender(columnData, lenderId) || EMPTY_OBJECT;
  return _get(paymentInfo, ['apr']);
};
export var hasErrorForSelectedLender = function hasErrorForSelectedLender(columnData) {
  var paymentInfo = getSelectedDownPaymentInfo(columnData);
  return !!_get(paymentInfo, ['error']);
};
export var getErrorMessageForSelectedLender = function getErrorMessageForSelectedLender(columnData) {
  var paymentInfo = getSelectedDownPaymentInfo(columnData);
  return _get(paymentInfo, ['error']);
};
export var hasWarningForSelectedLender = function hasWarningForSelectedLender(columnData) {
  var isGenericData = isGeneric(columnData);
  var isPositiveCashDeficiency = getCashDeficiency(columnData) > 0;
  return isGenericData || isPositiveCashDeficiency;
};
export var getRebatesForSelectedLender = function getRebatesForSelectedLender(columnData) {
  var selectedLenderId = getSelectedLenderId(columnData);
  var paymentInfo = getSelectedDownPaymentInfoForLender(columnData, selectedLenderId) || EMPTY_OBJECT;
  var rebates = _get(paymentInfo, ['rebates']) || EMPTY_ARRAY;

  var isRebatesValidated = _get(paymentInfo, 'rebatesVerified');

  return {
    rebates: rebates,
    isRebatesValidated: isRebatesValidated
  };
};
export var getAPRData = function getAPRData(columnData) {
  return columnData && (_get(getSelectedDownPaymentInfo(columnData), 'apr') || EMPTY_OBJECT);
};
export var getTaxesData = function getTaxesData(columnData, downpaymentId) {
  return columnData && (_get(getDownPaymentInfoForDownpaymentId(columnData, downpaymentId), 'taxes') || EMPTY_OBJECT);
};
export var getResidualData = function getResidualData(columnData) {
  return columnData && (_get(getSelectedDownPaymentInfo(columnData), 'residual') || EMPTY_OBJECT);
};
export var getResidualDataForLender = function getResidualDataForLender(columnData, lender) {
  var paymentInfo = getSelectedDownPaymentInfoForLender(columnData, lender) || EMPTY_OBJECT;
  return _get(paymentInfo, ['residual']);
};
export var getAquisitionFeeAmountForSelectedLender = function getAquisitionFeeAmountForSelectedLender(columnData, lender) {
  var paymentInfo = getSelectedDownPaymentInfoForLender(columnData, lender) || EMPTY_OBJECT;

  var aquisitionFee = _get(paymentInfo, ['bankFee']);

  return _get(aquisitionFee, ['amount']) || 0;
};
export var getProgramIdForSelectedLenderAndDownpayment = function getProgramIdForSelectedLenderAndDownpayment(columnData, downPaymentId) {
  var selectedLenderId = getSelectedLenderId(columnData);
  var programInfo = getProgramForLenderAndDownPaymentId(columnData, selectedLenderId, downPaymentId) || EMPTY_OBJECT;
  return _get(programInfo, ['programId']);
};
export var getProgramIdForLender = function getProgramIdForLender(columnData, lender) {
  var paymentInfo = getSelectedDownPaymentInfoForLender(columnData, lender) || EMPTY_OBJECT;
  return _get(paymentInfo, ['programId']);
};
export var getYearlyMilesInfoLender = function getYearlyMilesInfoLender(columnData, lender) {
  var paymentInfo = getSelectedDownPaymentInfoForLender(columnData, lender) || EMPTY_OBJECT;
  return _get(paymentInfo, ['yearlyMiles']);
};
export var isProgramForSelectedLenderGeneric = function isProgramForSelectedLenderGeneric(columnData) {
  return columnData && _get(getSelectedDownPaymentInfo(columnData), 'generic');
};
export var getYearlyMilesData = function getYearlyMilesData(columnData) {
  return columnData && _get(getSelectedDownPaymentInfo(columnData), 'yearlyMiles', EMPTY_OBJECT);
};
export var getColumnFees = function getColumnFees(columnData, downpaymentId) {
  return _get(getDownPaymentInfoForDownpaymentId(columnData, downpaymentId), ['dealFees']) || EMPTY_ARRAY;
};
export var getFnIProducts = function getFnIProducts(columnData) {
  var _columnData$fnIs = columnData.fnIs,
      fnIs = _columnData$fnIs === void 0 ? [] : _columnData$fnIs;
  return Array.isArray(fnIs) ? _toConsumableArray(fnIs) : [];
};
export var getFnIProductsBasedOnCategory = function getFnIProductsBasedOnCategory(columnData, category) {
  var fnIs = getFnIProducts(columnData);

  var categoryList = _castArray(category);

  return _filter(fnIs, function (fnI) {
    return _includes(categoryList, fnI.category) && !fnI.deleted;
  });
};
export var getFnIProductsBasedOnDisclosureType = function getFnIProductsBasedOnDisclosureType(columnData, disclosureType) {
  var fnIs = getFnIProducts(columnData);

  var disclosureTypes = _castArray(disclosureType);

  return _filter(fnIs, function (fnI) {
    return _includes(disclosureTypes, fnI.disclosureType) && !fnI.deleted;
  });
};
export var getLength = function getLength(array) {
  return array.length;
};
export var getSelectedDownPayment = function getSelectedDownPayment(columnData) {
  return _get(columnData, 'selectedDownPayment');
};
export var getSelectedDownPaymentID = function getSelectedDownPaymentID(columnData) {
  return _get(columnData, 'selectedDownPaymentId');
};
export var getColumnFNIs = function getColumnFNIs(columnData) {
  return _get(columnData, 'fnIs') || EMPTY_ARRAY;
};
export var getSelectedColumn = function getSelectedColumn() {
  var deskingPaymentDetails = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : EMPTY_ARRAY;
  return deskingPaymentDetails.find(function (_ref3) {
    var selected = _ref3.selected;
    return selected;
  }) || EMPTY_OBJECT;
};
export var getColumnDataById = function getColumnDataById() {
  var deskingPaymentDetails = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : EMPTY_ARRAY;
  var columnId = arguments.length > 1 ? arguments[1] : undefined;
  return deskingPaymentDetails.find(function (_ref4) {
    var id = _ref4.id;
    return columnId === id;
  }) || EMPTY_OBJECT;
};
export var getSelectedFees = function getSelectedFees(columnData) {
  var fees = _get(columnData, 'dealFees') || EMPTY_ARRAY;
  return fees.filter(function (fee) {
    return !fee.deleted;
  });
};
export var getCapCostReductionfactors = function getCapCostReductionfactors() {
  var columnData = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : EMPTY_OBJECT;
  var downPaymentInfo = getSelectedDownPaymentInfo(columnData) || EMPTY_OBJECT;
  return _get(downPaymentInfo, 'capCostReductionfactors') || EMPTY_OBJECT;
};
export var getAllFees = function getAllFees() {
  var columnData = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : EMPTY_OBJECT;
  var downPaymentInfo = getSelectedDownPaymentInfo(columnData) || {};
  return _get(downPaymentInfo, 'dealFees') || EMPTY_ARRAY;
};
export var getAllCappedFees = function getAllCappedFees() {
  var columnData = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : EMPTY_OBJECT;
  var allFees = getAllFees(columnData);
  return allFees.filter(function (_ref5) {
    var upfront = _ref5.upfront;
    return !upfront;
  });
};
export function getCapCostReduction() {
  var columnData = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : EMPTY_OBJECT;
  var downPaymentInfo = getSelectedDownPaymentInfo(columnData) || EMPTY_OBJECT;
  var downpaymentFromCash = _get(downPaymentInfo, 'capCostReductionfactors.downpaymentFromCash') || 0;
  var downpaymentFromEquity = _get(downPaymentInfo, 'capCostReductionfactors.downpaymentFromEquity') || 0;
  var downpaymentFromRebate = _get(downPaymentInfo, 'capCostReductionfactors.downpaymentFromRebate') || 0; // const downpaymentFromTax = _get(downPaymentInfo, 'capCostReductionfactors.downpaymentFromTax') || 0;

  return Money.add(downpaymentFromCash, Money.add(downpaymentFromEquity, downpaymentFromRebate));
}
export function getTotalRebate(columnData) {
  var downPaymentInfo = getSelectedDownPaymentInfo(columnData) || EMPTY_OBJECT;
  return DeskingHelpers.getNonDealerCashRebates(_get(downPaymentInfo, 'rebates') || []).reduce(function (acc, element) {
    return Money.add(acc, Number(element.rebateAmount));
  }, 0);
}
export var getRebatesSumInAllColumns = defaultMemoize(function (columns) {
  return _reduce(columns, function (acc, columnData) {
    return acc + _reduce(DeskingHelpers.getNonDealerCashRebates(_get(getSelectedDownPaymentInfo(columnData), 'rebates')), function (acc1, element) {
      return acc1 + Number(_get(element, 'rebateAmount'));
    }, 0);
  }, 0);
});
export function getInceptionFee(columnData) {
  var downPaymentInfo = getSelectedDownPaymentInfo(columnData) || EMPTY_OBJECT;
  return _get(downPaymentInfo, 'inceptionFee') || 0;
}
export function getSalesTax(columnData, downpaymentId) {
  var downPaymentInfo = getDownPaymentInfoForDownpaymentId(columnData, downpaymentId) || EMPTY_OBJECT;
  return _get(downPaymentInfo, ['taxes', 'salesTax']) || 0;
}
export function getTradeEquityCashBack(columnData, downpaymentId) {
  var downPaymentInfo = getDownPaymentInfoForDownpaymentId(columnData, downpaymentId) || EMPTY_OBJECT;
  return _get(downPaymentInfo, ['tradeEquityCashBack']);
}
export function getGaragedStateSalesTax(columnData) {
  var downPaymentInfo = getSelectedDownPaymentInfo(columnData) || EMPTY_OBJECT;
  return _get(downPaymentInfo, ['taxes', 'garagedStateSalesTax']) || 0;
}
export function getDocStampTax(columnData) {
  var downPaymentInfo = getSelectedDownPaymentInfo(columnData) || EMPTY_OBJECT;
  return _get(downPaymentInfo, ['taxes', 'docStampTax']) || 0;
}
export function getInventoryTax(columnData, downpaymentId) {
  var downPaymentInfo = getDownPaymentInfoForDownpaymentId(columnData, downpaymentId) || EMPTY_OBJECT;
  return _get(downPaymentInfo, ['taxes', 'inventoryTax']) || 0;
}
export function getOnePayPayment(columnData, downpaymentId) {
  var downPaymentInfo = getDownPaymentInfoForDownpaymentId(columnData, downpaymentId) || EMPTY_OBJECT;
  return _get(downPaymentInfo, ['onePayPayment']); // Do not return default 0
}
export function isInventoryTaxCapped(columnData) {
  var downPaymentInfo = getSelectedDownPaymentInfo(columnData) || EMPTY_OBJECT;
  return !_get(downPaymentInfo, ['taxes', 'inventoryTaxCapturedInDriveOff']) || false;
}
export function getInventoryTaxLoan(columnData) {
  if (isLoanOrCashOrBalloonPaymentType(columnData)) return getInventoryTax(columnData);
  return 0;
}
export function getInventoryTaxCapped(columnData) {
  if (isLeaseOrOPLeasePaymentType(columnData) && isInventoryTaxCapped(columnData)) return getInventoryTax(columnData);
  return 0;
}
export function getInventoryTaxUpfront(columnData) {
  if (isLeaseOrOPLeasePaymentType(columnData) && !isInventoryTaxCapped(columnData)) return getInventoryTax(columnData);
  return 0;
}
export function getLoanBusinessTax(columnData) {
  var inStateTax = getInStateLoanBusinessTax(columnData);
  var outStateTax = getOutOfStateLoanBusinessTax(columnData);
  return inStateTax + outStateTax;
}
export function getInStateLoanBusinessTax(columnData) {
  var downPaymentInfo = getSelectedDownPaymentInfo(columnData) || EMPTY_OBJECT;
  var keysToAdd = ['localBusinessTax', 'localBusinessAftermarketTax', 'stateBusinessTax'];
  return _reduce(keysToAdd, function (a, key) {
    return a + getNumber(_get(downPaymentInfo, ['taxes', key]));
  }, 0);
}
export function getOutOfStateLoanBusinessTax(columnData) {
  var downPaymentInfo = getSelectedDownPaymentInfo(columnData) || EMPTY_OBJECT;
  var keysToAdd = ['outOfStateLocalBusinessTax', 'outOfStateLocalBusinessAftermarketTax', 'outOfStateStateBusinessTax'];
  return _reduce(keysToAdd, function (a, key) {
    return a + getNumber(_get(downPaymentInfo, ['taxes', key]));
  }, 0);
}
export function getInStateLeaseCappedBusinessTax(columnData, downpaymentId) {
  var downPaymentInfo = getDownPaymentInfoForDownpaymentId(columnData, downpaymentId) || EMPTY_OBJECT;
  var keysToAdd = ['localBusinessTaxCapped', 'localBusinessAftermarketTaxCapped', 'stateBusinessTaxCapped'];
  return _reduce(keysToAdd, function (a, key) {
    return a + getNumber(_get(downPaymentInfo, ['taxes', key]));
  }, 0);
}
export function getOutOfStateLeaseCappedBusinessTax(columnData, downpaymentId) {
  var downPaymentInfo = getDownPaymentInfoForDownpaymentId(columnData, downpaymentId) || EMPTY_OBJECT;
  var keysToAdd = ['outOfStateLocalBusinessTaxCapped', 'outOfStateLocalBusinessAftermarketTaxCapped', 'outOfStateStateBusinessTaxCapped'];
  return _reduce(keysToAdd, function (a, key) {
    return a + getNumber(_get(downPaymentInfo, ['taxes', key]));
  }, 0);
}
export function getLeaseCappedBusinessTax(columnData, downpaymentId) {
  var inStateTax = getInStateLeaseCappedBusinessTax(columnData, downpaymentId);
  var outStateTax = getOutOfStateLeaseCappedBusinessTax(columnData, downpaymentId);
  return inStateTax + outStateTax;
}
export function getInStateLeaseUpfrontBusinessTax(columnData, downpaymentId) {
  var downPaymentInfo = getDownPaymentInfoForDownpaymentId(columnData, downpaymentId) || EMPTY_OBJECT;
  var keysToAdd = ['localBusinessTaxUpfront', 'localBusinessAftermarketTaxUpfront', 'stateBusinessTaxUpfront'];
  return _reduce(keysToAdd, function (a, key) {
    return a + getNumber(_get(downPaymentInfo, ['taxes', key]));
  }, 0);
}
export function getOutOfStateLeaseUpfrontBusinessTax(columnData, downpaymentId) {
  var downPaymentInfo = getDownPaymentInfoForDownpaymentId(columnData, downpaymentId) || EMPTY_OBJECT;
  var keysToAdd = ['outOfStateLocalBusinessTaxUpfront', 'outOfStateLocalBusinessAftermarketTaxUpfront', 'outOfStateStateBusinessTaxUpfront'];
  return _reduce(keysToAdd, function (a, key) {
    return a + getNumber(_get(downPaymentInfo, ['taxes', key]));
  }, 0);
}
export function getLeaseUpfrontBusinessTax(columnData, downpaymentId) {
  var inStateTax = getInStateLeaseUpfrontBusinessTax(columnData, downpaymentId);
  var outStateTax = getOutOfStateLeaseUpfrontBusinessTax(columnData, downpaymentId);
  return inStateTax + outStateTax;
}
export function getLoanLocalTax(columnData) {
  var downPaymentInfo = getSelectedDownPaymentInfo(columnData) || EMPTY_OBJECT;
  var keysToAdd = ['localTax', 'localAftermarketTax'];
  return _reduce(keysToAdd, function (a, key) {
    return a + getNumber(_get(downPaymentInfo, ['taxes', key]));
  }, 0);
}
export function getLeaseCappedLocalTax(columnData, downpaymentId) {
  var downPaymentInfo = getDownPaymentInfoForDownpaymentId(columnData, downpaymentId) || EMPTY_OBJECT;
  var keysToAdd = ['localTaxCapped', 'localAftermarketTaxCapped'];
  return _reduce(keysToAdd, function (a, key) {
    return a + getNumber(_get(downPaymentInfo, ['taxes', key]));
  }, 0);
}
export function getLeaseUpfrontLocalTax(columnData, downpaymentId) {
  var downPaymentInfo = getDownPaymentInfoForDownpaymentId(columnData, downpaymentId) || EMPTY_OBJECT;
  var keysToAdd = ['localTaxUpfront', 'localAftermarketTaxUpfront'];
  return _reduce(keysToAdd, function (a, key) {
    return a + getNumber(_get(downPaymentInfo, ['taxes', key]));
  }, 0);
}
export function getLoanSingleArticleTax(columnData) {
  var downPaymentInfo = getSelectedDownPaymentInfo(columnData) || EMPTY_OBJECT;
  return _get(downPaymentInfo, ['taxes', 'singleArticleTax']);
}
export function getLeaseCappedSingleArticleTax(columnData, downpaymentId) {
  var downPaymentInfo = getDownPaymentInfoForDownpaymentId(columnData, downpaymentId) || EMPTY_OBJECT;
  return _get(downPaymentInfo, ['taxes', 'singleArticleTaxCapped']);
}
export function getLeaseUpfrontSingleArticleTax(columnData, downpaymentId) {
  var downPaymentInfo = getDownPaymentInfoForDownpaymentId(columnData, downpaymentId) || EMPTY_OBJECT;
  return _get(downPaymentInfo, ['taxes', 'singleArticleTaxUpfront']);
} //-------

export function getDownpaymentTaxUpfront(columnData, downpaymentId) {
  var downPaymentInfo = getDownPaymentInfoForDownpaymentId(columnData, downpaymentId) || EMPTY_OBJECT;
  return _get(downPaymentInfo, ['taxes', 'downpaymentTax']) || 0;
}
export function getDownpaymentTaxCapped(columnData) {
  var downPaymentInfo = getSelectedDownPaymentInfo(columnData) || EMPTY_OBJECT;
  return _get(downPaymentInfo, ['taxes', 'downpaymentTaxCappedAmt']) || 0;
}
export function getServiceContractTax(columnData) {
  var downPaymentInfo = getSelectedDownPaymentInfo(columnData) || EMPTY_OBJECT;
  return _get(downPaymentInfo, ['taxes', 'serviceContractTax']) || 0;
}
export function getGSTTax(columnData) {
  var downPaymentInfo = getSelectedDownPaymentInfo(columnData) || EMPTY_OBJECT;
  return _get(downPaymentInfo, ['taxes', 'gstTax']) || 0;
}
export function getPSTTax(columnData) {
  var downPaymentInfo = getSelectedDownPaymentInfo(columnData) || EMPTY_OBJECT;
  return _get(downPaymentInfo, ['taxes', 'pstTax']) || 0;
}
export function getHSTTax(columnData) {
  var downPaymentInfo = getSelectedDownPaymentInfo(columnData) || EMPTY_OBJECT;
  return _get(downPaymentInfo, ['taxes', 'hstTax']) || 0;
}
export function getQSTTax(columnData) {
  var downPaymentInfo = getSelectedDownPaymentInfo(columnData) || EMPTY_OBJECT;
  return _get(downPaymentInfo, ['taxes', 'qstTax']) || 0;
}
export function getInsuranceTax(columnData) {
  var downPaymentInfo = getSelectedDownPaymentInfo(columnData) || EMPTY_OBJECT;
  return _get(downPaymentInfo, ['taxes', 'insuranceTax']) || 0;
}
export function getCountyTax(columnData) {
  var downPaymentInfo = getSelectedDownPaymentInfo(columnData) || EMPTY_OBJECT;
  return _get(downPaymentInfo, ['taxes', 'countyTax']) || 0;
}
export function getCityTax(columnData) {
  var downPaymentInfo = getSelectedDownPaymentInfo(columnData) || EMPTY_OBJECT;
  return _get(downPaymentInfo, ['taxes', 'cityTax']) || 0;
}
export var getStateTax = function getStateTax(columnData) {
  var _getSelectedDownPayme, _getSelectedDownPayme2;

  return ((_getSelectedDownPayme = getSelectedDownPaymentInfo(columnData)) === null || _getSelectedDownPayme === void 0 ? void 0 : (_getSelectedDownPayme2 = _getSelectedDownPayme.taxes) === null || _getSelectedDownPayme2 === void 0 ? void 0 : _getSelectedDownPayme2.stateTax) || 0;
};
export function getCountyTaxRate(columnData) {
  var downPaymentInfo = getSelectedDownPaymentInfo(columnData) || EMPTY_OBJECT;
  return _get(downPaymentInfo, ['taxes', 'countyTaxRate']) || 0;
} // export function getDownpaymentTaxInFees(columnData) {
//   const downPaymentInfo = getSelectedDownPaymentInfo(columnData) || EMPTY_OBJECT;
//   return _get(downPaymentInfo, ['taxes', 'downpaymentTaxInFees']) || 0;
// }

export function getFairFaxCountyTaxCapped(columnData, downpaymentId) {
  var downPaymentInfo = getDownPaymentInfoForDownpaymentId(columnData, downpaymentId) || EMPTY_OBJECT;
  return _get(downPaymentInfo, ['taxes', 'fairFaxCountyTaxCappedAmt']) || 0;
}
export function getFairFaxCountyTax(columnData, downpaymentId) {
  var downPaymentInfo = getDownPaymentInfoForDownpaymentId(columnData, downpaymentId) || EMPTY_OBJECT;
  return _get(downPaymentInfo, ['taxes', 'fairFaxCountyTax']) || 0;
}
export function getFairFaxCountyTaxUpfront(columnData, downpaymentId) {
  var downPaymentInfo = getDownPaymentInfoForDownpaymentId(columnData, downpaymentId) || EMPTY_OBJECT;
  return _get(downPaymentInfo, ['taxes', 'fairFaxCountyTaxUpfrontAmt']) || 0;
}
export function getOtherTaxInFees(columnData, downpaymentId) {
  var downPaymentInfo = getDownPaymentInfoForDownpaymentId(columnData, downpaymentId) || EMPTY_OBJECT;
  return _get(downPaymentInfo, ['taxes', 'otherTaxInFees']) || 0;
}
export function getCapCostTaxDriveOff(columnData, downpaymentId) {
  var _getCapCostTax = getCapCostTax(columnData, downpaymentId),
      driveOffTax = _getCapCostTax.driveOffTax;

  return driveOffTax;
}
export function getCapCostTaxCapped(columnData) {
  var _getCapCostTax2 = getCapCostTax(columnData),
      cappedTax = _getCapCostTax2.cappedTax;

  return cappedTax;
}
export function getCapCostTax(columnData, downpaymentId) {
  var downPaymentInfo = getDownPaymentInfoForDownpaymentId(columnData, downpaymentId) || EMPTY_OBJECT;
  var salesTaxUpFront = _get(downPaymentInfo, ['taxes', 'salesTaxCapturedInDriveOff']) || false;
  var salesTax = getSalesTax(columnData, downpaymentId);

  var customFeeTaxInFees = _get(downPaymentInfo, ['taxes', 'customFeeTaxInFees']);

  var customFeeTaxInCap = _get(downPaymentInfo, ['taxes', 'customFeeTaxInCap']);

  var isLeaseOrOpLeaseType = isLeaseOrOPLeasePaymentType(columnData);

  if (!isLeaseOrOpLeaseType) {
    return {
      driveOffTax: 0,
      cappedTax: 0
    };
  }
  /*
    conditions present at time of writing
    1.For In state deals, sum of all breakdown items was equal to salesTax.(any change in breakdown items value changes salestax)
      business Tax is already part of breakdown itemss and sales Tax.
    2.For Out Of State deals, sum of all breakdown items was NOT equal to salesTax(any change in breakdown items value DOES NOT change salestax )
      business Tax which comes in breakdown was not part of sales tax, hence we need to add it to sales tax to get capCost tax.
    3.For both instate n out of state deal, customFeeTax is not included in sales tax and not coming in breakdown also.
      we need to add it to sales tax to get total capcost
    4.Local tax and singleArticle tax is only for Instate Deals and they are already part of salesTax and breakdown.
    5.we  have to use taxBreakdownCappedTotal, taxBreakdownUpfrontTotal because sometimes we get data in both
      and in such case we cannot take salesTax directly
  */


  var taxBreakdownCappedTotal = getTaxBreakdownCappedTotal(columnData, downpaymentId);
  var taxBreakdownUpfrontTotal = getTaxBreakdownUpfrontTotal(columnData, downpaymentId);
  var outOfStateLeaseCappedBusinessTax = getOutOfStateLeaseCappedBusinessTax(columnData, downpaymentId);
  var outOfStateLeaseUpfrontBusinessTax = getOutOfStateLeaseUpfrontBusinessTax(columnData, downpaymentId);
  var cappedItemsNotInSalesTax = Money.add(customFeeTaxInCap, outOfStateLeaseCappedBusinessTax);
  var upfrontItemsNotInSalesTax = Money.add(customFeeTaxInFees, outOfStateLeaseUpfrontBusinessTax);
  var driveOffTax = salesTaxUpFront ? Money.add(salesTax, upfrontItemsNotInSalesTax) : upfrontItemsNotInSalesTax;
  var cappedTax = salesTaxUpFront ? cappedItemsNotInSalesTax : Money.add(salesTax, cappedItemsNotInSalesTax);

  if (taxBreakdownUpfrontTotal > 0 || taxBreakdownCappedTotal > 0) {
    cappedTax = Money.add(taxBreakdownCappedTotal, customFeeTaxInCap); // customFeeTaxInCap was not coming in breakdown
  }

  if (taxBreakdownUpfrontTotal > 0 || taxBreakdownCappedTotal > 0) {
    driveOffTax = Money.add(taxBreakdownUpfrontTotal, customFeeTaxInFees); // customFeeTaxInFees was not coming in breakdown
  }

  return {
    driveOffTax: driveOffTax,
    cappedTax: cappedTax
  };
}

function getTaxBreakdownCappedTotal(columnData, downpaymentId) {
  var downPaymentInfo = getDownPaymentInfoForDownpaymentId(columnData, downpaymentId) || EMPTY_OBJECT;
  return _get(downPaymentInfo, ['taxes', 'taxBreakdownCappedTotal']) || 0;
}

function getTaxBreakdownUpfrontTotal(columnData, downpaymentId) {
  var downPaymentInfo = getDownPaymentInfoForDownpaymentId(columnData, downpaymentId) || EMPTY_OBJECT;
  return _get(downPaymentInfo, ['taxes', 'taxBreakdownUpfrontTotal']) || 0;
}

export function getUseTax(columnData, downpaymentId) {
  var downPaymentInfo = _isNil(downpaymentId) ? getSelectedDownPaymentInfo(columnData) || EMPTY_OBJECT : getDownPaymentInfoForDownpaymentId(columnData, downpaymentId) || EMPTY_OBJECT;
  return _get(downPaymentInfo, ['taxes', 'useTax']) || 0;
}
export function getPropertyTax(columnData, downpaymentId) {
  var downPaymentInfo = getDownPaymentInfoForDownpaymentId(columnData, downpaymentId) || EMPTY_OBJECT;
  return _get(downPaymentInfo, ['taxes', 'propertyTax']) || 0;
}
export function getPayableAmount(columnData) {
  return Money.add(getSelectedDownPayment(columnData), getAmountFinanacedToDisplay(columnData)) || 0;
}
export function getTotalTax(columnData) {
  var downPaymentInfo = getSelectedDownPaymentInfo(columnData) || EMPTY_OBJECT;
  return _get(downPaymentInfo, 'totalTaxAmt') || 0;
}
export var getColumnAccessories = function getColumnAccessories(columnData) {
  return _get(columnData, 'accessories') || EMPTY_ARRAY;
};
export var getManuallyUpdatedColumnAccessories = function getManuallyUpdatedColumnAccessories(columnData) {
  return _filter(getColumnAccessories(columnData), function (_ref6) {
    var manuallyUpdated = _ref6.manuallyUpdated;
    return manuallyUpdated;
  });
};
export var getTaxableAccessoriesOfDisclosureTypeOther = function getTaxableAccessoriesOfDisclosureTypeOther(columnData) {
  return _filter(getColumnAccessories(columnData), function (_ref7) {
    var taxable = _ref7.taxable,
        disclosureType = _ref7.disclosureType;
    return taxable && _includes([DISCLOSURE_TYPES.OTHER], disclosureType);
  });
};
export var getAccessoriesExceptDisclosureTypeOther = function getAccessoriesExceptDisclosureTypeOther(columnData) {
  return _filter(getColumnAccessories(columnData), function (_ref8) {
    var taxable = _ref8.taxable,
        disclosureType = _ref8.disclosureType;
    return !(taxable && _includes([DISCLOSURE_TYPES.OTHER], disclosureType));
  });
};
export var getColumnAccessoriesIfPaymentTypesSupported = function getColumnAccessoriesIfPaymentTypesSupported(columnData, supportedPaymentTypes) {
  var paymentType = getPaymentType(columnData);

  if (supportedPaymentTypes.includes(paymentType)) {
    var accessories = getColumnAccessories(columnData);
    return accessories;
  }

  return [];
};
export var isLoanPaymentType = function isLoanPaymentType(columnData) {
  var paymentType = _get(columnData, ['paymentOption', 'paymentType']);

  return DeskingHelpers.isLoanPaymentType(paymentType);
};
export var isLeasePaymentType = function isLeasePaymentType(columnData) {
  var paymentType = _get(columnData, ['paymentOption', 'paymentType']);

  return DeskingHelpers.isLeasePaymentType(paymentType);
};
export var isLoanOrLeasePaymentType = function isLoanOrLeasePaymentType(columnData) {
  var paymentType = _get(columnData, ['paymentOption', 'paymentType']);

  return DeskingHelpers.isLoanOrLeasePaymentType(paymentType);
};
export var isLoanOrCashPaymentType = function isLoanOrCashPaymentType(columnData) {
  var paymentType = _get(columnData, ['paymentOption', 'paymentType']);

  return DeskingHelpers.isLoanOrCashPaymentType(paymentType);
};
export var isLeaseOrOPLeasePaymentType = function isLeaseOrOPLeasePaymentType(columnData) {
  var paymentType = _get(columnData, ['paymentOption', 'paymentType']);

  return DeskingHelpers.isLeseOrOPLeasePaymentType(paymentType);
};
export var isLeaseOrBalloonPaymentType = function isLeaseOrBalloonPaymentType(columnData) {
  var paymentType = _get(columnData, ['paymentOption', 'paymentType']);

  return DeskingHelpers.isLeaseOrBalloonPaymentType(paymentType);
};
export var isLeaseOrOPLeaseOrBalloonPaymentType = function isLeaseOrOPLeaseOrBalloonPaymentType(columnData) {
  var paymentType = _get(columnData, ['paymentOption', 'paymentType']);

  return DeskingHelpers.isLeaseOrOPLeaseOrBalloonPaymentType(paymentType);
};
export var isBalloonPaymentType = function isBalloonPaymentType(columnData) {
  var paymentType = _get(columnData, ['paymentOption', 'paymentType']);

  return DeskingHelpers.isBalloonPaymentType(paymentType);
};
export var isLoanOrLeaseOrBalloonPaymentType = function isLoanOrLeaseOrBalloonPaymentType(columnData) {
  var paymentType = _get(columnData, ['paymentOption', 'paymentType']);

  return DeskingHelpers.isLoanOrLeaseOrBalloonPaymentType(paymentType);
};
export var isLoanOrCashOrBalloonPaymentType = function isLoanOrCashOrBalloonPaymentType(columnData) {
  var paymentType = _get(columnData, ['paymentOption', 'paymentType']);

  return DeskingHelpers.isLoanOrCashOrBalloonPaymentType(paymentType);
};
export var isLoanOrBalloonPaymentType = function isLoanOrBalloonPaymentType(columnData) {
  var paymentType = _get(columnData, ['paymentOption', 'paymentType']);

  return DeskingHelpers.isLoanOrBalloonPaymentType(paymentType);
};
export var isCashPaymentType = function isCashPaymentType(columnData) {
  var paymentType = _get(columnData, ['paymentOption', 'paymentType']);

  return DeskingHelpers.isCashPaymentType(paymentType);
};
export var isOPLeasePaymentType = function isOPLeasePaymentType(columnData) {
  var paymentType = _get(columnData, ['paymentOption', 'paymentType']);

  return DeskingHelpers.isOnePayPaymentType(paymentType);
};
export var isFullPaymentType = function isFullPaymentType(columnData) {
  var paymentType = _get(columnData, ['paymentOption', 'paymentType']);

  return DeskingHelpers.isFullPaymentType(paymentType);
};
export var isColumnSelected = function isColumnSelected(columnData) {
  return _get(columnData, 'selected');
};
export var getSelectedTerm = function getSelectedTerm(columnData) {
  if (columnData) {
    return {
      term: _get(columnData, 'paymentOption.value'),
      durationType: _get(columnData, 'paymentOption.paymentFrequency')
    };
  }

  return '';
};
export var getTaxRate = function getTaxRate(columnData) {
  var taxes = getTaxesData(columnData);
  return _get(taxes, 'taxRate');
};
export var getTradeTaxExempt = function getTradeTaxExempt(columnData) {
  return _get(getTaxesData(columnData), 'tradeTaxExempt');
};
export var getTaxPercentApplicableForFee = function getTaxPercentApplicableForFee(columnData) {
  var paymentType = getPaymentType(columnData);
  var program = getSelectedDownPaymentInfo(columnData);
  var taxRate = 0;

  if (DeskingHelpers.isLoanOrLeaseOrBalloonPaymentType(paymentType)) {
    taxRate = _get(program, ['taxes', 'taxRate']) || 0;
  } else if (DeskingHelpers.isLeseOrOPLeasePaymentType(paymentType)) {
    taxRate = _get(program, ['taxes', 'capCostReductionTaxRate']) || 0;
  }

  return taxRate;
};
export var getAccessoriesGross = function getAccessoriesGross(columnData) {
  var grossType = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : GROSS_TYPES.FRONT;
  var accessories = _get(columnData, 'accessories') || EMPTY_ARRAY;
  var accessoriesOfType = accessories.filter(function (_ref9) {
    var profitType = _ref9.profitType;
    return profitType === grossType;
  });
  var totalSales = 0;
  var totalCost = 0;
  var totalDealerGross = 0;
  var breakUp = [];
  accessoriesOfType.forEach(function (accessory) {
    var cost = accessory.cost,
        sales = accessory.price;
    var dealerGross = Money.subtract(sales, cost);
    totalSales = Money.add(totalSales, sales);
    totalCost = Money.add(totalCost, cost);
    totalDealerGross = Money.add(totalDealerGross, dealerGross);
    breakUp.push({
      sales: sales,
      cost: cost,
      dealerGross: dealerGross,
      accessory: accessory
    });
  });
  return {
    totalSales: totalSales,
    totalCost: totalCost,
    totalDealerGross: totalDealerGross,
    breakUp: breakUp
  };
};
export var getFinanceReserveGross = function getFinanceReserveGross(columnData) {
  var financialReserve = getFinanceReserve(columnData);
  return {
    totalSales: financialReserve,
    totalCost: 0,
    totalDealerGross: financialReserve,
    breakUp: EMPTY_ARRAY
  };
};
export var getCashDeficiencyGross = function getCashDeficiencyGross(columnData) {
  var cashDeficiency = getCashDeficiency(columnData);
  return {
    totalSales: 0,
    totalCost: cashDeficiency,
    totalDealerGross: -cashDeficiency,
    breakUp: EMPTY_ARRAY
  };
};
export var getCappedInceptionFee = function getCappedInceptionFee(columnData) {
  var cashDeficiency = getCashDeficiency(columnData);
  return {
    totalSales: cashDeficiency,
    totalCost: 0,
    totalDealerGross: cashDeficiency,
    breakUp: EMPTY_ARRAY
  };
};
export var getDealCashGross = function getDealCashGross(columnData) {
  var columnRebates = getColumnRebates(columnData);
  var dealerCashRebates = DeskingHelpers.getDealerCashRebates(columnRebates) || EMPTY_ARRAY;
  var breakUp = [];
  dealerCashRebates.forEach(function (rebate) {
    var rebateAmount = rebate.rebateAmount,
        name = rebate.name;
    breakUp.push({
      sales: 0,
      cost: -rebateAmount,
      dealerGross: rebateAmount,
      name: name
    });
  });
  var totalDealerCashInRebates = dealerCashRebates.reduce(function (acc, element) {
    return Money.add(acc, Number(element.rebateAmount));
  }, 0);
  return {
    totalSales: 0,
    totalCost: -totalDealerCashInRebates,
    totalDealerGross: totalDealerCashInRebates,
    breakUp: breakUp
  };
};
export var getFNIGross = function getFNIGross(columnData) {
  var grossType = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : GROSS_TYPES.FRONT;
  var fnIs = _get(columnData, 'fnIs') || EMPTY_ARRAY;
  var fnisOfRequiredGrossType = fnIs.filter(function (_ref10) {
    var profitType = _ref10.profitType;
    return profitType === grossType;
  });
  var totalSales = 0;
  var totalCost = 0;
  var totalDealerGross = 0;
  var breakUp = [];
  fnisOfRequiredGrossType.forEach(function (fni) {
    var cost = fni.cost,
        sales = fni.price;
    var dealerGross = Money.subtract(sales, cost);
    totalSales = Money.add(totalSales, sales);
    totalCost = Money.add(totalCost, cost);
    totalDealerGross = Money.add(totalDealerGross, dealerGross);
    breakUp.push({
      sales: sales,
      cost: cost,
      dealerGross: dealerGross,
      fni: fni
    });
  });
  return {
    totalSales: totalSales,
    totalCost: totalCost,
    totalDealerGross: totalDealerGross,
    breakUp: breakUp
  };
};
export var getAcquistionFeeGross = function getAcquistionFeeGross(columnData) {
  var fees = getColumnFees(columnData);
  var requiredFee = findArrayItem(fees, 'internalFeeCode', INTERNAL_FEE_CODES.BANK_OR_ACQUISITION_FEE);

  var markup = _get(requiredFee, 'markup');

  return {
    totalSales: markup,
    totalCost: 0,
    totalDealerGross: markup,
    breakUp: EMPTY_ARRAY
  };
};
export var getColumnRebates = function getColumnRebates(columnData) {
  return _get(getSelectedDownPaymentInfo(columnData), ['rebates']) || EMPTY_ARRAY;
};
export function getMaxRateMarkup(columnData) {
  return _get(getSelectedDownPaymentInfo(columnData), ['maxRateMarkup']) || 0;
}
export function getPaidReserveIsCapCostParticipation(columnData) {
  return _get(getSelectedDownPaymentInfo(columnData), ['paidReserveIsCapCostParticipation']) || false;
}
export function getPaidReserveIsFlatFee(columnData) {
  return _get(getSelectedDownPaymentInfo(columnData), ['paidReserveIsFlatFee']) || false;
}
export var getFeeBasedOnInternalFeeCode = function getFeeBasedOnInternalFeeCode(columnData, internalFeeCode) {
  var dealFees = getColumnFees(columnData);
  var feeObj = dealFees.find(function (fee) {
    return fee.internalFeeCode === internalFeeCode;
  });
  if (feeObj) return _get(feeObj, 'amount');
  return undefined;
};
export var getFeeBasedOnFeeType = function getFeeBasedOnFeeType(columnData, feeType) {
  var dealFees = getColumnFees(columnData);

  var fees = _filter(dealFees, function (fee) {
    return fee.feeType === feeType;
  });

  return fees;
};

var getTotalFees = function getTotalFees(dealFees) {
  return dealFees.reduce(function (totalFee, _ref11) {
    var amount = _ref11.amount;
    return amount ? Money.add(totalFee, amount) : totalFee;
  }, 0);
};

export var getOtherFeesAmount = function getOtherFeesAmount(columnData) {
  // Total Fees excluding Documentation & Disposition Fee
  var totalFees = getTotalFees(getColumnFees(columnData));
  var documentFees = getFeeBasedOnInternalFeeCode(columnData, INTERNAL_FEE_CODES.DOCUMENTATION_FEE);
  var leaseContractFees = getFeeBasedOnFeeType(columnData, LEASE_CONTRACT);
  var sumOFLeaseContractFees = arraySumBasedOnKey(leaseContractFees, 0, 'amount');
  return getNumber(Money.subtract(totalFees, Money.add(documentFees, sumOFLeaseContractFees)));
};
export var getCappedStatusOfFee = function getCappedStatusOfFee(columnData, feeType) {
  var dealFees = getColumnFees(columnData);
  var feeObj = dealFees.find(function (_ref12) {
    var internalFeeCode = _ref12.internalFeeCode;
    return internalFeeCode === feeType;
  }) || EMPTY_OBJECT;
  return !_get(feeObj, 'upfront');
};
export var getFnIPriceBasedOnCategory = function getFnIPriceBasedOnCategory(columnData, category) {
  var products = getFnIProductsBasedOnCategory(columnData, category);
  return _reduce(products, function (totalPrice, fni) {
    return Money.add(totalPrice, fni.price || 0);
  }, 0);
};
export var getFnIPriceBasedOnDisclosureType = function getFnIPriceBasedOnDisclosureType(columnData, disclosureType) {
  var products = getFnIProductsBasedOnDisclosureType(columnData, disclosureType);
  return _reduce(products, function (totalPrice, fni) {
    return Money.add(totalPrice, fni.price || 0);
  }, 0);
};
export var getFnIPriceBasedOnExceptDisclosureTypes = function getFnIPriceBasedOnExceptDisclosureTypes(columnData, exceptDisclosureTypes) {
  var fnIs = getFnIProducts(columnData);
  fnIs = _filter(fnIs, function (fnI) {
    return !_includes(exceptDisclosureTypes, fnI.disclosureType) && !fnI.deleted;
  });
  return _reduce(fnIs, function (totalPrice, fni) {
    return Money.add(totalPrice, fni.price || 0);
  }, 0);
};
export function getBaseEMIAmount(columnData) {
  return _get(getSelectedDownPaymentInfo(columnData), ['baseEmiAmount']);
}
export function getCollectRegFee(columnData) {
  return _get(getSelectedDownPaymentInfo(columnData), ['collectRegFee']) || false;
}
export function getErrorMessageForSelectedDownpayment(columnData) {
  return _get(getSelectedDownPaymentInfo(columnData), ['error']);
}
export function getSelectedLenderId(columnData) {
  return _get(columnData, 'selectedLenderId');
}
export function getPreviousLenderId(columnData) {
  return _get(columnData, 'previousLenderId');
}
export function getDownPaymentType(columnData) {
  var paymentType = getPaymentType(columnData);
  return PAYMENT_TYPE_DOWNPAYMENT_TYPE_MAPPING[paymentType];
}
export function getDownpaymentFromCash() {
  var columnData = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : EMPTY_OBJECT;
  var downPaymentInfo = getSelectedDownPaymentInfo(columnData) || EMPTY_OBJECT;
  return _get(downPaymentInfo, 'capCostReductionfactors.downpaymentFromCash') || 0;
}
export function getDownpaymentFromEquity() {
  var columnData = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : EMPTY_OBJECT;
  var downPaymentInfo = getSelectedDownPaymentInfo(columnData) || EMPTY_OBJECT;
  return _get(downPaymentInfo, 'capCostReductionfactors.downpaymentFromEquity') || 0;
}
export function getDownpaymentFromRebate() {
  var columnData = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : EMPTY_OBJECT;
  var downPaymentInfo = getSelectedDownPaymentInfo(columnData) || EMPTY_OBJECT;
  return _get(downPaymentInfo, 'capCostReductionfactors.downpaymentFromRebate') || 0;
}
export function isSelectedProgramExpired(columnData) {
  return (_get(getSelectedDownPaymentInfo(columnData), 'programFlags') || EMPTY_ARRAY).includes(EXPIRED_PROGRAM_FLAG);
}
export function getHighestAmountFinanced(columnData) {
  var amountFinanced1 = getAmountFinanaced(columnData, 0);
  var amountFinanced2 = getAmountFinanaced(columnData, 1);
  var amountFinanced3 = getAmountFinanaced(columnData, 2);
  var allAmountFinanced = [getNumber(amountFinanced1), getNumber(amountFinanced2), getNumber(amountFinanced3)];

  var highestAmountFinanced = _last(allAmountFinanced.sort());

  return highestAmountFinanced;
}
export function getImbalanceAmount(columnData) {
  var downPaymentInfo = getSelectedDownPaymentInfo(columnData) || {};
  return downPaymentInfo.imbalanceAmount;
}
export function residualOverriden(columnData) {
  var downPaymentInfo = getSelectedDownPaymentInfo(columnData) || {};
  return _get(downPaymentInfo, 'residual.residualOverridden') || false;
}
export function residuaAdjustedPercentage(columnData, downpaymentId) {
  var downPaymentInfo = getDownPaymentInfoForDownpaymentId(columnData, downpaymentId);
  return _get(downPaymentInfo, 'residual.adjustedPercentage') || 0;
}
export function residuaAdjustedValue(columnData, downpaymentId) {
  var downPaymentInfo = getDownPaymentInfoForDownpaymentId(columnData, downpaymentId);
  return _get(downPaymentInfo, 'residual.adjustedValue') || 0;
}
export function getSelectedOutOfPocketCash(columnData) {
  return _get(getSelectedDownPaymentInfo(columnData), ['outOfPocketCash']);
}
/**
User can optionally select a lender for cash columns(lender is mandatory for all other type of columns like LOAN, LEASE, ONEPAY).

However due to existing tech problems,  we cannot remove lender info from columndata and hence
Columndata will always have some lender selected by default.

To Exlicitely say user has not selected any lender for cash column, we set osfLender=false

Why LenderId is mandatory ?
Due to data structure of state.desking.deskingPaymentDetails[someIndex].termPaymentDetails
 */

export function isLenderSelectedForCash(columnData) {
  return _get(getSelectedDownPaymentInfo(columnData), 'osfLender');
}
export function getFullTaxPaymentsCount(columnData, downpaymentId) {
  var downPaymentInfo = getDownPaymentInfoForDownpaymentId(columnData, downpaymentId) || EMPTY_OBJECT;
  return _get(downPaymentInfo, ['taxWaivedPayments', 'numberPaymentsFullTax']);
}
export function getPartialTaxPaymentsCount(columnData, downpaymentId) {
  var downPaymentInfo = getDownPaymentInfoForDownpaymentId(columnData, downpaymentId) || EMPTY_OBJECT;
  return _get(downPaymentInfo, ['taxWaivedPayments', 'numberPaymentsPartialTax']);
}
export function getTaxExemptPaymentsCount(columnData, downpaymentId) {
  var downPaymentInfo = getDownPaymentInfoForDownpaymentId(columnData, downpaymentId) || EMPTY_OBJECT;
  return _get(downPaymentInfo, ['taxWaivedPayments', 'numberPaymentsTaxExempt']);
}
export function getMonthlyPaymentWithFullTax(columnData, downpaymentId) {
  var downPaymentInfo = getDownPaymentInfoForDownpaymentId(columnData, downpaymentId) || EMPTY_OBJECT;

  var basePayment = _get(downPaymentInfo, ['taxWaivedPayments', 'basePayment']);

  return Money.add(basePayment, _get(downPaymentInfo, ['taxWaivedPayments', 'fullTaxOnPaymentAmount']));
}
export function getMonthlyPaymentWithPartialTax(columnData, downpaymentId) {
  var downPaymentInfo = getDownPaymentInfoForDownpaymentId(columnData, downpaymentId) || EMPTY_OBJECT;

  var basePayment = _get(downPaymentInfo, ['taxWaivedPayments', 'basePayment']);

  return Money.add(basePayment, _get(downPaymentInfo, ['taxWaivedPayments', 'partialTaxOnPaymentAmount']));
}
export function getMonthlyPaymentWithTaxExemption(columnData, downpaymentId) {
  var downPaymentInfo = getDownPaymentInfoForDownpaymentId(columnData, downpaymentId) || EMPTY_OBJECT;
  return _get(downPaymentInfo, ['taxWaivedPayments', 'basePayment']);
}
export function getDownPaymentInfoForDownpaymentId(columnData, downpaymentId) {
  var selectedTermPayment = getTermPaymentInfoForSelectedLender(columnData) || EMPTY_OBJECT;
  return selectedTermPayment[_isNil(downpaymentId) ? columnData.selectedDownPaymentId : downpaymentId];
}
export var getDeletedColumnFees = defaultMemoize(function (columnData) {
  return _get(getSelectedDownPaymentInfo(columnData), ['manuallyDeletedFees']) || EMPTY_ARRAY;
});
export var getTotalReabtesWithRebateType = function getTotalReabtesWithRebateType(columnData, rebateType) {
  return _reduce(getColumnRebates(columnData), function (totalRebates, rebate) {
    if (rebate.rebateType === rebateType) return totalRebates + rebate.rebateAmount;
    return totalRebates;
  }, 0);
};
export var getTotalReabtesWithTaxType = function getTotalReabtesWithTaxType(columnData, taxType) {
  return _reduce(getColumnRebates(columnData), function (totalRebates, rebate) {
    if (rebate.taxType === taxType) return totalRebates + rebate.rebateAmount;
    return totalRebates;
  }, 0);
};
export var getSelectedTier = function getSelectedTier(columnData) {
  return _get(columnData, 'selectedTier') || EMPTY_OBJECT;
};
export var getDownpayments = function getDownpayments(columnData) {
  var termPaymentDetailsForLender = getTermPaymentInfoForSelectedLender(columnData) || EMPTY_OBJECT;
  return _map(_keys(termPaymentDetailsForLender), function (downpaymentId) {
    return getDownPaymentInfoForDownpaymentId(columnData, downpaymentId).downPayment;
  });
};
export var getColumnTaxAndZipCodeType = function getColumnTaxAndZipCodeType(columnData) {
  return PAYMENT_TYPE_VS_TAX_TYPE[getPaymentType(columnData)];
};
export function getNonCCRRebates(columnData, downpaymentId) {
  var downPaymentInfo = getDownPaymentInfoForDownpaymentId(columnData, downpaymentId) || EMPTY_OBJECT;
  return _get(downPaymentInfo, ['driveOffReductionFactors', 'driveOffFromRebates']) || 0;
}
export function getTradeInEquity(columnData, downpaymentId) {
  var downPaymentInfo = getDownPaymentInfoForDownpaymentId(columnData, downpaymentId) || EMPTY_OBJECT;
  return _get(downPaymentInfo, ['driveOffReductionFactors', 'driveOffFromTradeIn']) || 0;
}
export function getLoanProcessingFee(columnData, downpaymentId) {
  var downPaymentInfo = getDownPaymentInfoForDownpaymentId(columnData, downpaymentId) || EMPTY_OBJECT;
  var fees = _get(downPaymentInfo, 'dealFees') || [];
  return _find(fees, function (fee) {
    return fee.internalFeeCode === INTERNAL_FEE_CODES.LOAN_PROCESSING_FEE;
  });
}
export function getAmountFinanacedToDisplay(columnData, optionalDownPaymentId) {
  var downPaymentId = _isNil(optionalDownPaymentId) ? getSelectedDownPaymentID(columnData) : optionalDownPaymentId;
  var amountFinanced = getAmountFinanaced(columnData, optionalDownPaymentId);

  if (isLoanPaymentType(columnData)) {
    var loanProcessingFee = _get(getLoanProcessingFee(columnData, downPaymentId), 'amount');

    return Money.subtract(amountFinanced, loanProcessingFee);
  }

  return amountFinanced;
}
export function getCATTax(columnData) {
  var downPaymentInfo = getSelectedDownPaymentInfo(columnData) || EMPTY_OBJECT;
  return _get(downPaymentInfo, ['taxes', 'catTax']) || 0;
}
export function getCATTaxCapped(columnData) {
  var downPaymentInfo = getSelectedDownPaymentInfo(columnData) || EMPTY_OBJECT;
  return _get(downPaymentInfo, ['taxes', 'catTaxCapped']) || 0;
}
export function getCATTaxUpfront(columnData) {
  var downPaymentInfo = getSelectedDownPaymentInfo(columnData) || EMPTY_OBJECT;
  return _get(downPaymentInfo, ['taxes', 'catTaxUpfront']) || 0;
}
export var getProgramManuallyOverridden = function getProgramManuallyOverridden(columnData) {
  return !!(columnData !== null && columnData !== void 0 && columnData.programManuallyOverridden);
};
export var getFordFlexDecreasePct = function getFordFlexDecreasePct(columnData) {
  var _getSelectedDownPayme3;

  return (_getSelectedDownPayme3 = getSelectedDownPaymentInfo(columnData)) === null || _getSelectedDownPayme3 === void 0 ? void 0 : _getSelectedDownPayme3.fordFlexDecreasePct;
};
export var getFordFlexFirstTerm = function getFordFlexFirstTerm(columnData) {
  var _getSelectedDownPayme4;

  return (_getSelectedDownPayme4 = getSelectedDownPaymentInfo(columnData)) === null || _getSelectedDownPayme4 === void 0 ? void 0 : _getSelectedDownPayme4.fordFlexFirstTerm;
};
export var getFordFlexFirstPayment = function getFordFlexFirstPayment(columnData) {
  var _getSelectedDownPayme5;

  return (_getSelectedDownPayme5 = getSelectedDownPaymentInfo(columnData)) === null || _getSelectedDownPayme5 === void 0 ? void 0 : _getSelectedDownPayme5.fordFlexFirstPayment;
};
export var getFordFlexLastPayment = function getFordFlexLastPayment(columnData) {
  var _getSelectedDownPayme6;

  return (_getSelectedDownPayme6 = getSelectedDownPaymentInfo(columnData)) === null || _getSelectedDownPayme6 === void 0 ? void 0 : _getSelectedDownPayme6.fordFlexLastPayment;
};
export var getFordFlexLastTerm = function getFordFlexLastTerm(columnData) {
  return Number(getTerm(columnData)) - Number(getFordFlexFirstTerm(columnData));
};
export var getProgramDesc = function getProgramDesc(columnData) {
  return columnData === null || columnData === void 0 ? void 0 : columnData.programDesc;
};
export var getTaxBreakupInfo = function getTaxBreakupInfo(columnData) {
  var downPaymentInfo = getSelectedDownPaymentInfo(columnData) || EMPTY_OBJECT;
  return _get(downPaymentInfo, ['taxes', 'taxDetails']) || [];
};